import type {
  AssignedSurveyResponse,
  CourseDetailResponse,
  SurveyListItem,
} from '@lms/api-client/backend';
import { Button } from '@lms/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@lms/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@lms/ui/dialog';
import { Label } from '@lms/ui/label';
import { useToast } from '@lms/ui/use-toast';
import { ChevronLeft, TrashIcon } from 'lucide-react';
import { useCallback, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSWRConfig } from 'swr';
import { useLocale, useTranslations } from 'use-intl';
import { DropZone } from '@/components/dropzone';
import { SimpleSelect } from '@/components/simple-select';
import type { Site } from '@/features/branding/brand-provider';
import {
  processScormPackage,
  removeCourse,
  saveCourseImage,
  saveScormPackage,
  updateCourseSurveyAssignment,
} from '@/features/courses/courses-api';
import {
  type Locale,
  supportedLanguages,
} from '@/features/locale/supported-languages';

type Props = {
  site: Site;
  course: CourseDetailResponse;
  surveys: SurveyListItem[];
  assignedSurvey?: AssignedSurveyResponse;
};

// Ports app/[brand]/admin/courses/_components/edit-course.tsx (CourseView). The
// Next server actions revalidated the path; here we revalidate the course's SWR
// key after mutations and track survey-save state with local state.
type SaveState = 'idle' | 'pending' | 'error';

export function CourseView({ site, course, surveys, assignedSurvey }: Props) {
  const locale = useLocale();
  const t = useTranslations();
  const { toast } = useToast();
  const { mutate } = useSWRConfig();
  const courseId = course.id;

  const revalidate = useCallback(
    () => mutate(['admin/course', courseId]),
    [mutate, courseId],
  );

  const [saveState, setSaveState] = useState<SaveState>('idle');

  const updateSurveyAssignment = useCallback(
    async (surveyId: number) => {
      setSaveState('pending');
      try {
        await updateCourseSurveyAssignment(courseId, surveyId);
        await revalidate();
        setSaveState('idle');
      } catch {
        setSaveState('error');
      }
    },
    [courseId, revalidate],
  );

  const processCourse = useCallback(
    async (url: string, filename: string, packageLocale: Locale) => {
      await saveScormPackage(url, filename, packageLocale, courseId);
      toast({
        title: t('addcourse.uploading_package'),
        description: t('addcourse.uploading_package_description'),
      });
      for (let i = 0; i < 2; i++) {
        try {
          await processScormPackage(url, packageLocale, courseId);
          toast({
            title: t('addcourse.processing_package'),
            description: t('addcourse.processing_package_description'),
          });
          break;
        } catch {
          if (i + 1 < 2) {
            toast({
              title: t('global.error'),
              description: t('addcourse.retrying_processing'),
            });
          } else {
            toast({
              title: t('global.error'),
              description: t('addcourse.failed_processing'),
            });
          }
        }
      }
      await revalidate();
    },
    [toast, courseId, t, revalidate],
  );

  const usedLocale =
    locale in (course?.data ?? {})
      ? locale
      : 'en' in (course?.data ?? {})
        ? 'en'
        : Object.keys(course?.data ?? {}).pop();

  return (
    <div className="mx-auto grid w-full max-w-screen-lg flex-1 auto-rows-max gap-4">
      <div className="flex items-center gap-4">
        <Button variant="outline" size="icon" className="h-7 w-7" asChild>
          <Link to={`/${site.slug}/admin/courses`}>
            <ChevronLeft className="h-4 w-4" />
            <span className="sr-only">{t('admin.go_back')}</span>
          </Link>
        </Button>
        <h1 className="flex-1 shrink-0 whitespace-nowrap text-xl font-semibold tracking-tight sm:grow-0">
          {usedLocale ? course?.data[usedLocale]?.name : '(NOT SET)'}
          {` (${usedLocale})`}
        </h1>
        <div className="hidden items-center justify-end gap-2 md:ml-auto md:flex">
          <RemoveButton courseId={courseId} slug={site.slug} />
          <Button variant="outline" asChild>
            <Link
              to={`/${site.slug}/course-preview/${courseId}`}
              target="_blank"
            >
              {t('addcourse.preview_course')}
            </Link>
          </Button>
        </div>
      </div>

      <div>
        <Card className="mb-8 w-full">
          <CardHeader>
            <CardTitle>{t('addcourse.image')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-6">
              <div className="grid gap-3">
                <DropZone
                  onUploaded={async (url) => {
                    await saveCourseImage(url, courseId);
                    await revalidate();
                  }}
                  currentImage={course?.image}
                  folder="courseimage"
                  tag={`course-${courseId}`}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="mb-8 w-full">
          <CardHeader>
            <CardTitle>{t('survey.survey')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div>
              <Label>{t('survey.survey')}</Label>
              <SimpleSelect
                defaultValue={assignedSurvey?.id.toString()}
                options={[
                  { value: '-1', label: t('admin.none') },
                  ...surveys.map((v) => ({
                    value: `${v.id}`,
                    label: v.name,
                  })),
                ]}
                placeholder="Select survey"
                onValueChange={(v) =>
                  updateSurveyAssignment(Number.parseInt(v, 10))
                }
              />
            </div>
            <div className="mt-2">
              {saveState === 'pending' ? (
                <span className="text-sm">{t('addcourse.saving')}</span>
              ) : saveState === 'error' ? (
                <span className="text-destructive text-sm">
                  {t('addcourse.failed_saving')}
                </span>
              ) : (
                <span className="text-muted-foreground text-sm">
                  {t('addcourse.saved')}
                </span>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="w-full">
          <CardHeader>
            <CardTitle>{t('addcourse.scorm_packages')}</CardTitle>
          </CardHeader>
          <CardContent>
            {supportedLanguages.map((l) => (
              <div className="mb-8" key={l}>
                <Label>{t(`languages.${l}`)}</Label>
                <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
                  <DropZone
                    onUploaded={(url, filename) =>
                      processCourse(url, filename, l)
                    }
                    folder="courses"
                    accept="application/zip"
                    variant="file"
                    currentImage={course?.data[l]?.filename}
                    tag={`course-${courseId}-${l}`}
                  />
                  <div className="flex flex-col">
                    {course?.data[l]?.zip ? (
                      <>
                        <div>
                          <b>
                            {t('admin.name')}
                            {': '}
                          </b>
                          {course.data[l]?.name ?? t('addcourse.loading')}
                        </div>
                        <div>
                          <b>
                            {t('addcourse.filename')}
                            {': '}
                          </b>
                          {course.data[l]?.filename}
                        </div>
                      </>
                    ) : (
                      <div>{t('addcourse.nothing_uploaded')}</div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function RemoveButton({
  courseId,
  slug,
}: {
  courseId: number | 'new';
  slug: string;
}) {
  const [warningOpen, setWarningOpen] = useState(false);
  const t = useTranslations('admin');
  const navigate = useNavigate();

  const handleRemove = async () => {
    await removeCourse(courseId as number);
    navigate(`/${slug}/admin/courses`);
  };

  return (
    <>
      <Dialog open={warningOpen} onOpenChange={setWarningOpen}>
        <DialogTrigger />
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('are_you_sure')}</DialogTitle>
          </DialogHeader>
          <Button
            variant="destructive"
            className="w-full"
            onClick={handleRemove}
          >
            <TrashIcon className="mr-2 h-4 w-4" />
            {t('remove', { what: '' }).trim()}
          </Button>
        </DialogContent>
      </Dialog>
      <Button
        variant="destructive"
        disabled={courseId === 'new'}
        onClick={() => setWarningOpen(true)}
      >
        {t('remove', { what: t('course').toLowerCase() })}
      </Button>
    </>
  );
}
